export type Params = Record<string, string | number | boolean>;

export interface RouteConfig {
  name: string;
  path?: string;
}

// Replace path paramters such as `:siteId`
function replacePathParams(route: string, data: Params = {}): string {
  return route.replace(/(:[a-zA-Z]+)/g, (urlParam) => {
    const index = route.indexOf(urlParam);
    const paramKey = urlParam.slice(1);
    const datum = data[paramKey];

    if (datum !== undefined) {
      return String(datum);
    }

    const currentParamEndIndex = index + urlParam.length;

    if (datum === undefined && currentParamEndIndex === route.length) {
      return '';
    }

    throw new Error(`No parameter provided for: '${paramKey}' in URL path '${route}'`);
  });
}

function replaceQueryParams(route: string, data: Params = {}): string {
  const queryParamStart = route.indexOf('?');

  // Replace query parameters in the given route.
  if (queryParamStart !== -1) {
    const searchParams = new URLSearchParams(route.substring(queryParamStart + 1));

    const toDelete: Array<string> = [];
    searchParams.forEach((_, key) => {
      const datum = data[key];
      if (datum !== undefined) {
        searchParams.set(key, String(datum));
      } else {
        /**
         * We push keys onto an array rather than calling `searchParams.delete()` because calling delete
         * gets the indexes out of sync and the loop skips values in the array.
         */
        toDelete.push(key);
      }
    });

    toDelete.forEach((key) => searchParams.delete(key));

    const searchParamString = searchParams.toString();

    if (searchParamString.length > 0) {
      return `${route.substring(0, queryParamStart)}?${searchParams.toString()}`;
    }

    return route.substring(0, queryParamStart);
  }

  return route;
}

/*
Copy of getRoute from @hotjar/webapp to avoid circular reference
*/
// eslint-disable-next-line func-params-args/func-params
export function getRoute(route: string, data: Params = {}, searchParams?: string): string {
  const parameterizedPath = replaceQueryParams(replacePathParams(route, data), data);

  const separator = parameterizedPath.indexOf('?') === -1 ? '?' : '&';
  const params = searchParams === undefined ? '' : searchParams.substring(1);
  return parameterizedPath + separator + params;
}
